import { ColumnDef } from '@tanstack/react-table'
import { AdvertisementWithMedia } from '@magnetar/admin-api-interfaces'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useState } from 'react'
import { AdvertisementDialog } from './AdvertisementDialog'
import { ArrowUpDown, Settings } from 'lucide-react'

// 創建一個事件匯流排，用於跨組件通信
export type AdvertisementAction = {
  type: 'edit' | 'delete'
  advertisementId: number
  supplierId: number
}

export type AdvertisementColumnsProps = {
  onAction: (action: AdvertisementAction) => void
  onRefresh?: () => void
}

export const createColumns = ({
  onAction,
  onRefresh,
}: AdvertisementColumnsProps): ColumnDef<AdvertisementWithMedia>[] => [
  {
    accessorKey: 'advertisement_id',
    header: 'ID',
    cell: ({ row }) => {
      const id: number = row.getValue('advertisement_id')
      return (
        <Badge variant="outline" className="font-medium">
          {id}
        </Badge>
      )
    },
  },
  {
    accessorKey: 'title',
    header: '廣告標題',
    enableSorting: false,
    size: 160,
    minSize: 120,
    maxSize: 240,
    cell: ({ row }) => {
      const title: string = row.getValue('title')
      return (
        <div className="w-[160px] truncate" title={title}>
          {title}
        </div>
      )
    },
  },
  {
    accessorKey: 'description',
    header: '廣告描述',
    enableSorting: false,
    size: 200,
    minSize: 140,
    maxSize: 300,
    cell: ({ row }) => {
      const description: string = row.getValue('description')
      return (
        <div className="w-[200px] truncate" title={description}>
          {description}
        </div>
      )
    },
  },
  {
    accessorKey: 'media',
    header: '媒體數',
    enableSorting: false,
    cell: ({ row }) => {
      const media = row.original.media
      return (
        <Badge variant="outline" className="font-medium">
          {media?.length ?? 0}
        </Badge>
      )
    },
  },
  {
    accessorKey: 'monthlyPlayCount',
    header: '當月播放',
    enableSorting: false,
    cell: ({ row }) => (
      <Badge variant="outline" className="font-medium">
        {row.original.monthlyPlayCount}
      </Badge>
    ),
  },
  {
    accessorKey: 'broadcastDeviceCount',
    header: '裝置數',
    enableSorting: false,
    cell: ({ row }) => (
      <Badge variant="outline" className="font-medium">
        {row.original.broadcastDeviceCount}
      </Badge>
    ),
  },
  {
    id: 'date_range',
    header: '廣告期間',
    enableSorting: false,
    cell: ({ row }) => {
      const { start_date, end_date } = row.original
      if (!start_date && !end_date) return <span className="text-muted-foreground">-</span>
      const format = (d: string | Date) =>
        new Date(d).toISOString().substring(0, 10).replace(/-/g, '/')
      return (
        <span className="whitespace-nowrap">
          {start_date ? format(start_date) : '-'} ~ {end_date ? format(end_date) : '-'}
        </span>
      )
    },
  },
  {
    accessorKey: 'created_at',
    header: ({ column }) => {
      return (
        <div className="flex items-center">
          建立時間
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="ml-1 p-0"
          >
            <ArrowUpDown className="h-4 w-4" />
          </Button>
        </div>
      )
    },
    enableSorting: true,
    cell: ({ row }) => {
      const date: string = row.getValue('created_at')
      const d = new Date(date)
      return (
        <span className="whitespace-nowrap" title={d.toLocaleString()}>
          {d.toISOString().substring(0, 10).replace(/-/g, '/')}
        </span>
      )
    },
  },
  {
    accessorKey: 'is_active',
    header: '狀態',
    enableSorting: false,
    cell: ({ row }) => {
      const is_active: boolean = row.getValue('is_active')
      return (
        <Badge variant={is_active ? 'default' : 'secondary'}>{is_active ? '啟用' : '停用'}</Badge>
      )
    },
  },
  {
    id: 'actions',
    enableSorting: false,
    cell: function ActionsCell({ row }) {
      const advertisement = row.original
      const [open, setOpen] = useState(false)

      return (
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <Settings className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>操作</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => setOpen(true)}>編輯廣告</DropdownMenuItem>
              <DropdownMenuItem
                className="text-destructive"
                onSelect={() =>
                  onAction({
                    type: 'delete',
                    advertisementId: advertisement.advertisement_id,
                    supplierId: advertisement.supplier_id,
                  })
                }
              >
                刪除廣告
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <AdvertisementDialog
            open={open}
            onOpenChange={setOpen}
            advertisementId={advertisement.advertisement_id}
            onRefresh={onRefresh}
          />
        </div>
      )
    },
  },
]
